<template>
  <v-card flat outlined class="mt-5">
    <v-card-title>
      Add a record to this {{ source }}<v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="type"
            :items="types"
            outlined
            dense
            hide-details="auto"
            label="Record Type"
            @change="fetchHandler"
          />
        </v-col>
        <v-col cols="12">
          <AppAutocomplete
            v-if="showSearchInput"
            v-model="selectedRecords"
            :items="items"
            label="Choose associated items"
            item-text="name"
            return-object
            outlined
            :loading="loading"
            hide-details
            hide-no-data
            no-filter
            small-chips
            hide-selected
            multiple
            dense
            @update:search-input-debounced="keywordChangeHandler"
            @input="selectedRecordWatchHandler"
          >
            <template #item="{ item }">
              <AssociatedItem :item="item" />
            </template>
            <template #selection="{ item }">
              <CommonChip small>
                <AssociatedItem :item="item" />
              </CommonChip>
            </template>
          </AppAutocomplete>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import { ServiceFactory } from '@/services/ServiceFactory';
import { getAssociatedTypes } from '@/bundles/Tasks/helpers/associatedTypes';
import { NotificationMutations } from '@/store/types/mutation-types';
import { MIN_CHAR_COUNT } from '@/bundles/App/consts';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import AssociatedItem from '@/bundles/Tasks/components/associated/AssociatedItem.vue';
import AppAutocomplete from '@/bundles/App/components/common/AppAutocomplete.vue';

const TaskService = ServiceFactory.get('task');

export default {
  name: 'AssociatedForm',

  components: { AssociatedItem, CommonChip, AppAutocomplete },

  props: {
    collection: {
      type: Array,
      default: () => [],
    },
    allowAll: Boolean,
    // Names the parent entity in the form title, e.g. 'task' or 'note'.
    source: {
      type: String,
      default: 'task',
    },
  },

  data () {
    return {
      items: [],
      type: 'All Types',
      selectedRecords: [],
      loading: false,
      searchInput: '',
    };
  },

  computed: {
    ...mapState({
      token: (state) => state.idToken,
    }),
    types () {
      return getAssociatedTypes(this.allowAll);
    },
    showSearchInput () {
      return (
        (this.allowAll && this.type === 'All Types') ||
        (!this.allowAll && this.type !== '' && this.type !== 'All Types')
      );
    }
  },

  methods: {
    ...mapMutations('Notifications', {
      addNotification: NotificationMutations.ADD_NOTIFICATION
    }),
    fetchHandler () {
      this.selectedRecords = [];
      this.fetch();
    },
    keywordChangeHandler (value) {
      if (value?.length >= MIN_CHAR_COUNT) {
        this.searchInput = value;
        this.fetchHandler();
      }
    },
    fetch: async function () {
      try {
        this.loading = true;
        const params = [
          { name: 'keyword', value: this.searchInput },
          { name: 'type', value: this.type },
          { name: 'take', value: 20 },
        ];

        const { data } = await TaskService.getAssociated({
          params,
          token: this.token
        });

        this.items = data.result;
      } catch (error) {
        const notification = { ...error };
        this.addNotification(notification);
      } finally {
        this.loading = false;
      }
    },
    getDuplicateRecord () {
      const lastSelectedRecord = this.selectedRecords[this.selectedRecords.length - 1];

      return this.collection.find((record) => record._key === lastSelectedRecord._key);
    },
    selectedRecordWatchHandler (records) {
      if (!records.length) {
        return;
      }

      const duplicatedRecord = this.getDuplicateRecord();

      if (duplicatedRecord) {
        this.addNotification({
          status: 0,
          title: 'Error',
          message: `${duplicatedRecord.name} is already added`
        });

        this.selectedRecords = this.selectedRecords.filter((record) => record._key !== duplicatedRecord._key);
        return;
      }

      this.$emit('addAssociated', records);
    }
  },
};
</script>
